// ============================================================
// ELC Shift — Uploads / Balances module (v100.0)
//
// TOIL + leave balance management. Two layers:
//
//   1. DATA: loadBalancesFromDB pulls balance rows; saveBalanceForStaff
//      writes a single staff member's balance via the worker /api/balances
//      endpoint.
//
//   2. UI: the staff-balance modal — open/close/save handlers for TOIL,
//      holiday, and sick leave — plus the admin "TOIL overview" table
//      that lists every staff member's current TOIL position.
//
// TOIL display rule (v81.3 — pure-rota formula):
//   toil_balance = 8 × (PH dates worked) - 8 × (TOIL days taken)
// formatToilDH renders integers as days-and-hours: 13h → "+1d 5h", etc.
//
// Public surface:
//   Uploads.balances.loadFromDB()             — fetch + cache balances
//   Uploads.balances.saveForStaff(id, b)      — persist one staff's balance
//   Uploads.balances.formatToilDH(h)          — h → "Xd Yh" display
//   Uploads.balances.renderToilOverview()     — admin TOIL overview table
//   Uploads.balances.openModal(staffId)       — open per-staff balance modal
//   Uploads.balances.closeModal()             — close modal
//   Uploads.balances.saveModalToil()          — save the manual TOIL
//                                                adjustment (v129.36; the rota
//                                                half stays computed)
//   Uploads.balances.saveModalHoliday()       — save holiday from modal
//   Uploads.balances.saveModalSick()          — save sick from modal
//
// Names dropped "Balances" prefix where redundant since they're under
// Uploads.balances.X — e.g. openBalancesModal → openModal.
//
// Free globals consumed:
//   - DB, showToast, staffData, escapeHtml, _safeStaffName,
//     getStoreCountry, loadStaffFromDB (cache refresh after save)
// ============================================================

import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy

// v129.36 — the APL report for the whole scope, fetched once per balances
// refresh and shared by every modal open. Declared HERE, at the top: `let` is
// hoisted into a temporal dead zone, so loadFromDB() clearing it below a
// declaration further down the file would have thrown ReferenceError on the
// first balances load.
let _aplCache = null;

// v136.8 (boot audit): `prefetched` (optional) is the DB.balances.list(year) read
// already under way: admin.html's boot starts it alongside the other loads and
// applies it once staffData has landed. Absent, it reads as before.
export async function loadFromDB(prefetched) {
  _aplCache = null;             // a balances refresh re-reads APL too
  const year = new Date().getFullYear();
  try {
    const rows = await (prefetched && typeof prefetched.then === 'function' ? prefetched : DB.balances.list(year));
    if (!Array.isArray(rows)) return;
    rows.forEach(r => {
      // v129.37 — TWO display bugs, both found while diagnosing the 83/96/102
      // canary on 3 Aug. The DATA was right in both cases; the screen lied.
      //
      // (1) `|| 30` fires on a genuine ZERO. Two people in the 11 Jul reconcile
      //     carry holiday_remaining 0 (staff 112, 113 — new joiners). Their
      //     holiday_total computes to 0, `0 || 30` is 30, and the Balances tab
      //     showed them 30 days they do not have. `!= null` distinguishes
      //     "absent" (fall back to the default) from "zero" (a real figure).
      //
      // (2) Math.max(0, …) HID an overdraw. HR's own sheet records staff 83,
      //     96 and 102 as overdrawn on holiday (-3, -5, -3); the reconcile
      //     stored that faithfully, and then this line clamped it to 0 on the
      //     way to the screen. Somebody 5 days over looked exactly like
      //     somebody with nothing left. On a pay-adjacent screen the negative
      //     IS the information — it is what tells a manager to stop approving.
      const holTotal  = (r.holiday_total != null) ? Number(r.holiday_total) : 30;
      const sickTotal = (r.sick_total    != null) ? Number(r.sick_total)    : 14;
      const holRemaining = holTotal - (r.holiday_used || 0);
      const sickRemaining = sickTotal - (r.sick_used || 0);
      localStorage.setItem('elc_hol_bal_'   + r.staff_id, String(holRemaining));
      localStorage.setItem('elc_hol_total_' + r.staff_id, String(holTotal));
      localStorage.setItem('elc_sick_bal_'  + r.staff_id, String(sickRemaining));
      localStorage.setItem('elc_sick_total_'+ r.staff_id, String(sickTotal));
      // Write toil_balance onto the staff record if loaded
      const s = staffData.find(x => x.id === r.staff_id);
      if (s) {
        s.toilBalance = r.toil_balance || 0;
        // v129.36 — the same number decomposed, so the modal can show WHICH
        // half an editor is changing. Optional: an older Worker (or a cached
        // response from before this deploy) omits them and the modal falls
        // back to deriving the rota figure from balance − adjust.
        s.toilEarned = r.toil_earned_hours != null ? Number(r.toil_earned_hours) : null;
        s.toilTaken  = r.toil_taken_hours  != null ? Number(r.toil_taken_hours)  : null;
        s.toilAdjust = r.toil_adjust != null ? Number(r.toil_adjust) : 0;
        // v129.68: the fourth decomposition term — approved shift-variance TOIL,
        // computed from flagged rota rows. 0 from an older Worker.
        s.toilVariance = r.toil_variance_hours != null ? Number(r.toil_variance_hours) : 0;
      }
    });
    dlog('[DB] Loaded balances for ' + rows.length + ' staff');
  } catch(e) {
    console.warn('[DB] Balances load failed:', e.message);
  }
}

// v127.27 (27 Jul audit P1): persist ONLY what was edited. The old writer
// rebuilt EVERY column from per-device localStorage (defaults 30/14), so any
// balance save from a stale/fresh till clobbered the HR-reconciled
// holiday_total/sick_total — and the toil_balance it carried was a DEAD column
// (the server recomputes TOIL from the rota + toil_adjust). merge-duplicates
// preserves the columns a row doesn't carry, so a partial row is the safe
// write. `edited` says which family was touched:
//   { toilDelta: ±hours }              — manual TOIL grant → server folds into toil_adjust
//   { holiday: { total, days } }       — holiday modal (total IS the edited value here)
//   { sick:    { total, days } }       — sick modal
// v129.107 — RETURNS THE PROMISE. It used to swallow every failure into a
// console.warn while the callers had already shown a green "saved" toast, so a
// refused write looked identical to a successful one and the figure only
// snapped back on the next loadFromDB — which is how a broken admin balance
// write survived unnoticed. A pay-affecting write must never report success it
// has not had. Callers await this and say what actually happened.
export function saveForStaff(staffId, edited) {
  if (!edited) return Promise.resolve(false);   // no callers left that save "everything"
  const year = new Date().getFullYear();
  const row = { staff_id: staffId, year: year };
  if (edited.toilDelta != null) {
    row.toil_adjust_delta = Math.round(edited.toilDelta * 10) / 10;
  }
  if (edited.holiday) {
    row.holiday_total = edited.holiday.total;
    row.holiday_used  = Math.max(0, edited.holiday.total - edited.holiday.days);
  }
  if (edited.sick) {
    row.sick_total = edited.sick.total;
    row.sick_used  = Math.max(0, edited.sick.total - edited.sick.days);
  }
  return DB.balances.save([row]);
}

export function formatToilDH(h) {
  if (!h) return '0h';
  var sign = h < 0 ? '-' : '+';
  var abs = Math.abs(h);
  var days = Math.floor(abs / 8);
  var hrs  = Math.round((abs - days * 8) * 10) / 10;
  var hrsStr = (hrs % 1 === 0) ? String(hrs) : hrs.toFixed(1);
  if (days === 0) return sign + hrsStr + 'h';
  if (hrs === 0)  return sign + days + 'd';
  return sign + days + 'd ' + hrsStr + 'h';
}

export function renderToilOverview() {
  const areaFilter  = document.getElementById('toilOverviewArea')?.value  || '';
  const storeFilter = document.getElementById('toilOverviewStore')?.value || '';
  const nameSearch  = (document.getElementById('toilSearchName')?.value   || '').toLowerCase();
  const flagFilter  = document.getElementById('toilOverviewFlag')?.value  || '';
  const list        = document.getElementById('toilOverviewList');

  // Populate store dropdown once
  const storeSel = document.getElementById('toilOverviewStore');
  if (storeSel && storeSel.options.length <= 1) {
    const stores = [...new Set(staffData.map(s => s.store))].sort();
    stores.forEach(st => {
      const o = document.createElement('option');
      o.value = st; o.textContent = st; storeSel.appendChild(o);
    });
  }

  // Load and display entitlement defaults
  loadEntitlementFields();

  // Filter staff
  const visible = staffData.filter(s => {
    if (s.active === false) return false;
    if (areaFilter  && s.area  !== areaFilter)  return false;
    if (storeFilter && s.store !== storeFilter)  return false;
    if (nameSearch  && !s.name.toLowerCase().includes(nameSearch)) return false;

    const holBal  = parseInt(localStorage.getItem('elc_hol_bal_'+s.id)  || '30');
    const sickBal = parseInt(localStorage.getItem('elc_sick_bal_'+s.id) || '14');
    const toilBal = s.toilBalance || 0;
    if (flagFilter === 'low_hol'   && holBal  > 5)  return false;
    if (flagFilter === 'low_sick'  && sickBal > 2)  return false;
    if (flagFilter === 'high_toil' && toilBal <= 16) return false;
    return true;
  });

  if (!visible.length) {
    list.innerHTML = '<div class="card" style="text-align:center;padding:40px;color:var(--sub)">No staff match these filters</div>';
    return;
  }

  const rows = visible.map(s => {
    const toilBal  = s.toilBalance || 0;
    const toilColor = toilBal > 0 ? 'var(--green)' : toilBal < 0 ? 'var(--red)' : 'var(--sub)';
    const toilWarn  = toilBal > 16 ? ' ⚠️' : '';

    const holBal   = parseInt(localStorage.getItem('elc_hol_bal_'+s.id)   || '30');
    const holTotal = parseInt(localStorage.getItem('elc_hol_total_'+s.id) || '30');
    // v129.37 — an OVERDRAWN person reads differently from a nearly-empty one.
    // Until now they could not: the figure was clamped at 0 upstream, so -5 and
    // 0 looked identical. "5 over" is an instruction to stop approving leave;
    // "0 left" is not.
    const holColor = holBal <= 5 ? 'var(--red)' : holBal <= 10 ? '#d97706' : 'var(--green)';
    const holWarn  = holBal < 0 ? ' ⛔' : holBal <= 5 ? ' ⚠️' : '';

    const sickBal   = parseInt(localStorage.getItem('elc_sick_bal_'+s.id)   || '14');
    const sickTotal = parseInt(localStorage.getItem('elc_sick_total_'+s.id) || '14');
    const sickColor = sickBal <= 2 ? 'var(--red)' : sickBal <= 5 ? '#d97706' : '#0891b2';
    const sickWarn  = sickBal <= 2 ? ' ⚠️' : '';

    return `<tr>
      <td>
        <div translate="no" style="font-weight:700">${escapeHtml(s.name||'')}</div>
        <div style="font-size:11px;color:var(--sub)"><span>${escapeHtml(s.role||'')}</span> · <span translate="no">${escapeHtml((s.store||'').split(' - ').pop())}</span></div>
      </td>
      <td>
        <span style="font-weight:700;color:${toilColor}">${formatToilDH(toilBal)}${toilWarn}</span>
      </td>
      <td>
        <span style="font-weight:700;color:${holColor}">${holBal}${holWarn}</span>
        <span style="font-size:10px;color:var(--sub)"> / ${holTotal}d</span>
      </td>
      <td>
        <span style="font-weight:700;color:${sickColor}">${sickBal}${sickWarn}</span>
        <span style="font-size:10px;color:var(--sub)"> / ${sickTotal}d</span>
      </td>
      <td>
        <button class="btn btn-outline btn-sm" onclick="Uploads.balances.openModal(${s.id})">Edit</button>
      </td>
    </tr>`;
  });

  list.innerHTML = `
    <div style="font-size:12px;color:var(--sub);margin-bottom:8px">${visible.length} staff · ⚠️ = needs attention</div>
    <div class="card" style="padding:0;overflow:hidden">
      <table class="data-table">
        <thead><tr>
          <th>Staff</th>
          <th>⏱ TOIL</th>
          <th>🌴 Holiday</th>
          <th>🤒 Sick</th>
          <th></th>
        </tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
    </div>`;
}

export function openModal(staffId) {
  _bmStaffId = staffId;
  const s = staffData.find(x => x.id === staffId);
  if (!s) return;
  document.getElementById('balancesModalTitle').textContent = 'Edit Balances';
  document.getElementById('balancesModalName').textContent = s.name + ' — ' + s.store;
  // v129.36 — TOIL is editable again, but only its ADJUSTMENT half. The rota
  // figure stays computed and unwritable; see the note in the modal markup for
  // why the two are shown apart rather than as one total.
  const adjust = Number(s.toilAdjust) || 0;
  // v129.68: the rota-derived figure now includes approved shift-variance TOIL
  // (toilVariance, computed server-side from flagged rows) so the modal's
  // identity still holds: rota + adjust = balance. The older-Worker fallback
  // (balance − adjust) already includes variance implicitly.
  const rota = (s.toilEarned != null && s.toilTaken != null)
    ? (s.toilEarned - s.toilTaken + (Number(s.toilVariance) || 0))
    : ((s.toilBalance || 0) - adjust);          // older Worker: derive it
  document.getElementById('bmToilEarned').textContent =
    s.toilEarned != null ? '+' + formatToilDH(s.toilEarned) : '—';
  document.getElementById('bmToilTaken').textContent =
    s.toilTaken != null ? (s.toilTaken ? '-' + formatToilDH(s.toilTaken) : '0h') : '—';
  document.getElementById('bmToilRota').textContent = formatToilDH(rota);
  document.getElementById('bmToilAdjust').value = adjust;
  document.getElementById('bmToilBalance').textContent = formatToilDH(s.toilBalance || 0);
  loadModalApl(staffId);
  document.getElementById('bmHolDays').value = localStorage.getItem('elc_hol_bal_'+staffId) || '30';
  document.getElementById('bmHolEntitlement').value = localStorage.getItem('elc_hol_total_'+staffId) || '30';
  document.getElementById('bmSickDays').value = localStorage.getItem('elc_sick_bal_'+staffId) || '14';
  document.getElementById('bmSickEntitlement').value = localStorage.getItem('elc_sick_total_'+staffId) || '14';
  document.getElementById('balancesModal').classList.add('open');
}

export function closeModal() {
  document.getElementById('balancesModal').classList.remove('open');
  _bmStaffId = null;
}

// v129.36 — rebuilt. The v81.3 version was ORPHANED: its four inputs
// (bmToilType/Hours/Date/Reason) had been deleted from the modal years of
// versions ago, so every line here referenced a null element.
//
// The field is the TOTAL adjustment, but the write is a DELTA
// (toil_adjust_delta), for two reasons that are worth keeping:
//   • The server folds a delta additively onto whatever it reads, with a
//     fail-CLOSED read (v128.11). Sending an absolute value would let a stale
//     modal — one opened before someone else's edit — silently overwrite it.
//   • The delta path is the audited one (balance_edit in write_audit_log).
// So: newTotal − oldTotal, and a no-op edit sends nothing at all.
export function saveModalToil() {
  const s = staffData.find(x => x.id === _bmStaffId);
  if (!s) return;
  const el = document.getElementById('bmToilAdjust');
  const raw = el ? String(el.value).trim() : '';
  if (raw === '') { showToast('Enter an adjustment in hours (0 for none)', 'error'); return; }
  const next = Number(raw);
  if (!isFinite(next)) { showToast('Enter a number of hours', 'error'); return; }
  // A one-shift correction is 8h; anything past a fortnight of them is a typo,
  // and a typo here silently changes what somebody is owed.
  if (Math.abs(next) > 200) { showToast('That is more than 200 hours — check the figure', 'error'); return; }
  const prev = Number(s.toilAdjust) || 0;
  const delta = Math.round((next - prev) * 10) / 10;
  if (delta === 0) { showToast('No change to save'); return; }

  // Optimistic local update so the modal and the table agree immediately; the
  // authoritative figure arrives on the next loadFromDB.
  const prevBalance = s.toilBalance;
  s.toilAdjust = next;
  s.toilBalance = Math.round(((s.toilBalance || 0) + delta) * 10) / 10;
  document.getElementById('bmToilBalance').textContent = formatToilDH(s.toilBalance);
  renderToilOverview();
  // v129.107 — the toast waits for the SERVER. It used to fire immediately, so a
  // refused write read as "TOIL adjustment saved" and the AM had no way to know
  // the store user would never see it. On failure the optimistic figures are put
  // back where the server actually is, not where the click left them.
  saveForStaff(_bmStaffId, { toilDelta: delta }).then(function () {
    showToast('TOIL adjustment saved — ' + s.name + ' (' + (delta > 0 ? '+' : '') + delta + 'h)', 'success');
  }).catch(function (e) {
    s.toilAdjust = prev;
    s.toilBalance = prevBalance;
    const el2 = document.getElementById('bmToilBalance');
    if (el2) el2.textContent = formatToilDH(s.toilBalance);
    renderToilOverview();
    showToast('TOIL NOT saved — ' + ((e && e.message) || 'the server refused the write') + '. Nothing changed for ' + s.name + '.', 'error');
  });
}

// v129.36 — Authorised Paid Leave for ONE staffer, in the balances modal.
//
// APL deducts from no balance, so there is no remaining/entitlement pair to
// edit; the only meaningful figure is how much has been used. Read-only by
// design — the record of it is the rota.
//
// Sourced from /admin/apl-usage, which is already Bearer-gated and AM-scoped.
// Deliberately NOT folded into /api/balances: that endpoint is TOKENLESS, and
// APL reasons include pregnancy and bereavement.
export async function loadModalApl(staffId) {
  const el = document.getElementById('bmAplBody');
  if (!el) return;
  el.textContent = 'Loading…';
  try {
    if (!_aplCache) {
      let token = '';
      try {
        token = adminToken();   // v136.12: the shared read (DB token first)
      } catch (e) { /* fall through — the fetch will 401 and we say so */ }
      const res = await fetch(DB.workerUrl() + '/admin/apl-usage', {
        cache: 'no-store', headers: { 'Authorization': 'Bearer ' + token },
      });
      const d = await res.json();
      if (!res.ok || !d || d.ok === false) throw new Error((d && d.error) || ('HTTP ' + res.status));
      _aplCache = d;
    }
    // Only render if this modal is still the one open (a fast Edit → Edit).
    if (_bmStaffId !== staffId) return;
    const row = (_aplCache.rows || []).find(r => r.staff_id === staffId);
    if (!row || !row.days) {
      el.innerHTML = 'No authorised paid leave recorded in ' + _aplCache.year + '.';
      return;
    }
    const reasons = Object.keys(row.reasons || {})
      .sort((a, b) => row.reasons[b] - row.reasons[a])
      .map(r => '<span>' + escapeHtml(r) + ' &times;' + row.reasons[r] + '</span>')
      .join(' &middot; ');
    el.innerHTML =
      '<div style="display:flex;justify-content:space-between"><span>Days taken in ' + _aplCache.year + '</span>' +
      '<strong style="color:var(--text);font-size:14px">' + row.days + '</strong></div>' +
      (reasons
        ? '<div style="margin-top:6px;padding-top:6px;border-top:1px dashed var(--adm-info-line)">' + reasons + '</div>'
        : (_aplCache.reasons_unavailable
          ? '<div style="margin-top:6px;font-size:11px">Reasons unavailable — migration v180-0 has not run.</div>'
          : '')) +
      '<div style="font-size:11px;margin-top:6px;line-height:1.4">APL does not deduct from any balance. ' +
      'It is recorded on the rota, so it is changed there.</div>';
  } catch (e) {
    // Say what failed. "No leave recorded" and "the read broke" must never look
    // the same — that indistinguishability was the v129.18 APL lesson.
    if (_bmStaffId !== staffId) return;
    el.innerHTML = '<span style="color:var(--red,#b91c1c)">Could not load authorised leave &mdash; <span translate="no">' +
      escapeHtml((e && e.message) || 'unknown error') + '</span></span>';
  }
}

// v129.107 — same honesty fix as saveModalToil: these two were showing "saved"
// before the write resolved, so the admin app's silent balance failure looked
// like success here too. The localStorage copies are per-device display state,
// not the record, so they are only written once the SERVER has taken it.
export function saveModalHoliday() {
  const days = parseInt(document.getElementById('bmHolDays').value);
  const total = parseInt(document.getElementById('bmHolEntitlement').value);
  saveForStaff(_bmStaffId, { holiday: { total: total, days: days } }).then(function () {
    localStorage.setItem('elc_hol_bal_'+_bmStaffId, days);
    localStorage.setItem('elc_hol_total_'+_bmStaffId, total);
    showToast('Holiday balance saved', 'success');
    renderToilOverview();
  }).catch(function (e) {
    showToast('Holiday balance NOT saved — ' + ((e && e.message) || 'the server refused the write'), 'error');
  });
}

export function saveModalSick() {
  const days = parseInt(document.getElementById('bmSickDays').value);
  const total = parseInt(document.getElementById('bmSickEntitlement').value);
  saveForStaff(_bmStaffId, { sick: { total: total, days: days } }).then(function () {
    localStorage.setItem('elc_sick_bal_'+_bmStaffId, days);
    localStorage.setItem('elc_sick_total_'+_bmStaffId, total);
    showToast('Sick balance saved', 'success');
    renderToilOverview();
  }).catch(function (e) {
    showToast('Sick balance NOT saved — ' + ((e && e.message) || 'the server refused the write'), 'error');
  });
}


// ── 🕊️ APL usage report (v129.15, an area manager's idea) ──────────────────────────
// Authorised Paid Leave deducts from no balance; the control is visibility.
// Fetches /admin/apl-usage (AM-scoped server-side) and renders per-staff days
// with a reason breakdown. Token read mirrors devReports' adminToken fallback.
export async function loadAplUsage() {
  const el = document.getElementById('aplUsageContent');
  if (!el) return;
  el.innerHTML = '<div style="font-size:12px;color:var(--sub)">Loading…</div>';
  let token = '';
  try {
    token = adminToken();   // v136.12: the shared read (DB token first)
  } catch (e) { /* fall through */ }
  try {
    const res = await fetch(DB.workerUrl() + '/admin/apl-usage', {
      cache: 'no-store', headers: { 'Authorization': 'Bearer ' + token },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d || d.ok !== true) {
      el.innerHTML = '<div style="font-size:12px;color:var(--red);word-break:break-word">Could not load: <span translate="no">' + escapeHtml((d && d.error) || ('HTTP ' + res.status)) + '</span></div>';
      return;
    }
    // v129.18: say WHY when reasons are missing. Before the v180-0 migration
    // the column doesn't exist, so every reason is dropped on save — without
    // this note the report looks like a clean bill of health for the one
    // control on a leave type that deducts from no balance.
    const migrationNote = d.reasons_unavailable
      ? '<div style="font-size:12px;color:var(--amber,#b45309);margin-bottom:8px">'
        + '⚠️ Reasons are not being recorded yet — migration <code>v180-0</code> has not run on this database. '
        + 'The leave days below are complete; the reason column is not.</div>'
      : '';
    if (!d.rows || !d.rows.length) {
      el.innerHTML = migrationNote + '<div style="font-size:12px;color:var(--sub)">No Authorised Paid Leave recorded in ' + escapeHtml(String(d.year)) + '.</div>';
      return;
    }
    const reasonChip = (r, n) =>
      '<span style="font-size:11px;background:var(--adm-teal-bg);color:var(--adm-teal-text);padding:2px 8px;border-radius:6px;margin-inline-end:4px">' +
      escapeHtml(r.charAt(0).toUpperCase() + r.slice(1)) + ' ×' + n + '</span>';
    el.innerHTML = migrationNote +
      '<div class="table-wrap"><table class="data-table">' +
      '<thead><tr><th>Staff</th><th>Store</th><th>Days (' + escapeHtml(String(d.year)) + ')</th><th>Reasons</th><th>Dates</th></tr></thead><tbody>' +
      d.rows.map((r) =>
        '<tr><td translate="no" style="font-weight:600">' + escapeHtml(r.name || '') + '</td>' +
        '<td translate="no">' + escapeHtml((r.store || '').split(' - ').pop()) + '</td>' +
        '<td style="font-weight:700">' + Number(r.days) + '</td>' +
        '<td>' + Object.entries(r.reasons || {}).map(([k, n]) => reasonChip(k, n)).join('') + '</td>' +
        '<td style="font-size:11px;color:var(--sub)">' + (r.dates || []).map(escapeHtml).join(', ') + '</td></tr>'
      ).join('') +
      '</tbody></table></div>';
  } catch (e) {
    el.innerHTML = '<div style="font-size:12px;color:var(--red)">Could not load: <span translate="no">' + escapeHtml((e && e.message) || String(e)) + '</span></div>';
  }
}
